import { useMemo } from "react";
import { Card, Grid } from "@mui/material";
import { Autocomplete, Box, Button, DataTable, FormField, IPropListContainer } from "@maysoft/common-component-react";

import useStrings from "../../hooks/useStrings";
import TableCell from "../../components/TableCell";
import { Mode } from "../../constants/enums";
import { Constants } from "../../constants/constants";
import { useData, useSelectData } from "../../hooks/messageTemplateCase/list";
import { IRequestGetPagedMessageTemplateCase } from "../../services/message/MessageTemplateCase.service";


export interface IMessageTemplateCaseListContainerProps extends Omit<IPropListContainer<IRequestGetPagedMessageTemplateCase>, "onNavigate"> {
    onNavigate: (data?: { id?: string; mode?: Mode, tenantCode?: string; serviceCode?: string; }) => void;
}

const MessageTemplateCaseListContainer = ({ requestGetPaged, onGetPaged, onNavigate }: IMessageTemplateCaseListContainerProps) => {
    const Strings = useStrings();

    const { messageTemplateTypeData } = useSelectData();

    const { model, rows, getPaged, handleDelete, onChangeValue, onResetModel, isServiceAdmin, tenantCodenames, serviceCodenames } = useData({ requestGetPaged, onGetPaged });
    const filterForm = () => (
        <Box>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <FormField
                        maxLength={255}
                        label={Strings.Common.SEARCH_TEXT}
                        placeholder={Strings.Common.ENTER_SEARCH_TEXT}
                        defaultValue={model.requestDataTemp?.searchText}
                        onBlur={(value) => onChangeValue(value, "searchText")}
                    />
                </Grid>
                {isServiceAdmin && <Grid item xs={12}>
                    <Autocomplete
                        data={tenantCodenames || []}
                        defaultValue={model?.requestDataTemp?.tenantCode}
                        label={Strings.MESSAGE_TEMPLATE.TENANT}
                        placeholder={Strings.MESSAGE_TEMPLATE.SELECT_TENANT}
                        onChange={(value) => onChangeValue(value, "tenantCode")}
                    />
                </Grid>}
                {isServiceAdmin && <Grid item xs={12}>
                    <Autocomplete
                        data={serviceCodenames || []}
                        defaultValue={model?.requestDataTemp?.serviceCode}
                        label={Strings.MESSAGE_TEMPLATE.SERVICE}
                        placeholder={Strings.MESSAGE_TEMPLATE.SELECT_SERVICE}
                        onChange={(value) => onChangeValue(value, "serviceCode")}
                    />
                </Grid>}
            </Grid>
        </Box>
    );

    const columns = useMemo(
        () => [
            {
                Header: Strings.MESSAGE_TEMPLATE_CASE.TYPE,
                accessor: "templateType",
                width: "100px",
                Cell: ({ value }: any) => <TableCell value={value} data={messageTemplateTypeData} isCodeName />,
            },
            {
                Header: Strings.MESSAGE_TEMPLATE_CASE.MESSAGE_TEMPLATE,
                accessor: "templateName",
                width: "150px",
            },
            {
                Header: Strings.MESSAGE_TEMPLATE_CASE.CASE,
                accessor: "caseName",
                width: "200px",
            },
        ],
        [Strings, messageTemplateTypeData]
    );

    const table = useMemo(
        () => ({
            columns,
            rows: rows || [],
        }),
        [rows, columns]
    );

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "end", mb: 3 }}>
                <Button
                    onClick={() => {
                        onNavigate({ id: undefined, mode: Mode.Create, tenantCode: model?.requestData?.tenantCode, serviceCode: model?.requestData?.serviceCode });
                    }}
                >
                    {Strings.Common.ADD_NEW}
                </Button>
            </Box>
            <Card>
                <Box p={2}>
                    <DataTable
                        isShowIndex
                        totalCount={model.requestData?.totalCount}
                        pageNumber={model.requestData?.pageNumber}
                        rowPerPage={model.requestData?.pageSize || Constants.ROW_PER_PAGE}
                        onChangePageSize={(pageSize) => getPaged({ ...model.requestData, pageSize })}
                        onChangePageNumber={(pageNumber) => getPaged({ ...model.requestData, pageNumber })}
                        searchText={model.requestData?.searchText}
                        placeholderSearch={Strings.Common.SEARCH_TEXT}
                        onSearch={(val) => getPaged({ ...model.requestData, pageNumber: 1, searchText: val })}
                        onSearchChange={(value) => onChangeValue(value, "searchText")}
                        filterForm={filterForm()}
                        onFilter={() => getPaged({ ...model.requestDataTemp, pageNumber: 1 })}
                        onCloseFilter={onResetModel}
                        onReset={() =>
                            getPaged({
                                ...model.requestData,
                                tenantCode: undefined,
                                serviceCode: undefined,
                                pageNumber: 1,
                            })
                        }
                        table={table}
                        actionList={[
                            {
                                key: "detail",
                                iconName: "view",
                                title: Strings.Common.VIEW_DETAIL,
                                callback: (row) => onNavigate({ id: row?.id, mode: Mode.View }),
                            },
                            {
                                key: "edit",
                                iconName: "edit",
                                title: Strings.Common.EDIT,
                                callback: (row) => onNavigate({ id: row?.id, mode: Mode.Update }),
                            },
                            {
                                key: "delete",
                                iconName: "delete",
                                title: Strings.Common.DELETE,
                                callback: (row) => handleDelete(row.id),
                            },
                        ]}
                    />
                </Box>
            </Card>
        </Box>
    );
};

export default MessageTemplateCaseListContainer;
